import { type LDContext, useLDClient } from 'launchdarkly-react-client-sdk';
import { isEqual, noop } from 'lodash';
import {
  createContext,
  memo,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { convertUnknownToError, reportErrorToBugsnag } from '@air/utils-error';

import { isDevOrTestStage } from '~/swr-hooks/utils';

type LaunchDarklyProviderProps = PropsWithChildren<object>;

export type LaunchDarklyProviderContextValue = {
  identify: (context: LDContext) => void;
};

const defaultValue: LaunchDarklyProviderContextValue = {
  identify: noop,
};

const LaunchDarklyProviderContext = createContext<LaunchDarklyProviderContextValue>(defaultValue);

export const LaunchDarklyProvider = memo(({ children }: LaunchDarklyProviderProps) => {
  const ldClient = useLDClient();
  const userRef = useRef<LDContext>();

  const [debouncedIdentify] = useDebouncedCallback(async () => {
    if (!userRef.current || !ldClient) {
      return;
    }
    if (isEqual(userRef.current, ldClient.getContext())) {
      return;
    }

    try {
      await ldClient.identify(userRef.current);
    } catch (error) {
      const typedError = convertUnknownToError(error);
      // ignore network error to prevent unhandled promise being thrown and logging that to bugsnag
      if (typedError.message !== 'network error (Error)') {
        throw error;
      }
    }
  }, 250);

  // When ldClient becomes available after SDK init, identify with any pending context.
  // The debounced identify may have fired before ldClient was ready.
  useEffect(() => {
    if (ldClient && userRef.current) {
      debouncedIdentify();
    }
  }, [ldClient, debouncedIdentify]);

  const identify = useCallback(
    (user: LDContext) => {
      const userObject = {
        kind: 'user',
        appVersion: process.env.APP_VERSION,
        platform: 'web',
        ...userRef.current,
        ...user,
      };

      if (!isEqual(userObject, userRef.current)) {
        userRef.current = userObject;

        debouncedIdentify();
      }
    },
    [debouncedIdentify],
  );

  const value = useMemo(() => ({ identify }), [identify]);

  return <LaunchDarklyProviderContext.Provider value={value}>{children}</LaunchDarklyProviderContext.Provider>;
});

LaunchDarklyProvider.displayName = 'LaunchDarklyProvider';

export const useLaunchDarklyContext = () => {
  const context = useContext(LaunchDarklyProviderContext);

  if (context === defaultValue) {
    const error = 'LaunchDarklyProviderContext used outside of LaunchDarklyProvider';

    if (isDevOrTestStage()) {
      throw error;
    } else {
      reportErrorToBugsnag({
        error,
        context: error,
      });
    }
  }

  return context;
};
